import { useEffect, useMemo, useState } from 'react';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  techdocsStorageApiRef,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { appTranslationRef } from '../../translation';

export type DocsVersion = {
  version: string;
  title: string;
  aliases: string[];
};

const safePathPattern =
  /^[A-Za-z0-9][A-Za-z0-9._-]*(\/[A-Za-z0-9][A-Za-z0-9._-]*)*$/;

const normalizeDocsPath = (path: string) => path.replace(/^\/+|\/+$/g, '');

const isSafePath = (path: string) =>
  safePathPattern.test(path) &&
  path.split('/').every(segment => segment !== '.' && segment !== '..');

export function parseDocsVersions(value: unknown): DocsVersion[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<string>();

  return value.flatMap(item => {
    if (!item || typeof item !== 'object') {
      return [];
    }

    const candidate = item as Partial<DocsVersion>;
    const version = candidate.version?.trim();
    const title = candidate.title?.trim();

    if (
      !version ||
      !title ||
      !isSafePath(version) ||
      !Array.isArray(candidate.aliases) ||
      !candidate.aliases.every(
        alias => typeof alias === 'string' && isSafePath(alias),
      ) ||
      seen.has(version)
    ) {
      return [];
    }

    seen.add(version);
    return [{ version, title, aliases: candidate.aliases }];
  });
}

export function findCurrentVersion(
  versions: DocsVersion[],
  docsPath: string,
): DocsVersion | undefined {
  const normalizedPath = normalizeDocsPath(docsPath);
  const latestVersion =
    versions.find(version => version.aliases.includes('latest')) ?? versions[0];

  if (!normalizedPath) {
    return latestVersion;
  }

  const candidates = versions
    .flatMap(version =>
      [version.version, ...version.aliases].map(path => ({
        path,
        version,
      })),
    )
    .sort((left, right) => right.path.length - left.path.length);

  return (
    candidates.find(
      candidate =>
        normalizedPath === candidate.path ||
        normalizedPath.startsWith(`${candidate.path}/`),
    )?.version ?? latestVersion
  );
}

export function getReaderBasePath(pathname: string, docsPath: string): string {
  const normalizedPathname = pathname.replace(/\/+$/g, '');
  const normalizedDocsPath = normalizeDocsPath(docsPath);

  if (!normalizedDocsPath) {
    return normalizedPathname;
  }

  const pathnameSegments = normalizedPathname.split('/');
  const docsPathSegments = normalizedDocsPath.split('/');

  if (docsPathSegments.length >= pathnameSegments.length) {
    return normalizedPathname;
  }

  return pathnameSegments.slice(0, -docsPathSegments.length).join('/');
}

export function getVersionRootPath(
  pathname: string,
  docsPath: string,
  version: string,
): string {
  return `${getReaderBasePath(pathname, docsPath)}/${version}/`;
}

export const TechDocsVersionSelector = () => {
  const { t } = useTranslationRef(appTranslationRef);
  const { entityRef } = useTechDocsReaderPage();
  const techdocsStorageApi = useApi(techdocsStorageApiRef);
  const fetchApi = useApi(fetchApiRef);
  const location = useLocation();
  const navigate = useNavigate();
  const { '*': docsPath = '' } = useParams();
  const [versions, setVersions] = useState<DocsVersion[]>([]);

  useEffect(() => {
    let active = true;

    const loadVersions = async () => {
      try {
        const storageUrl = (await techdocsStorageApi.getStorageUrl()).replace(
          /\/+$/g,
          '',
        );
        const entityPath = [entityRef.namespace, entityRef.kind, entityRef.name]
          .map(part => encodeURIComponent(part))
          .join('/');
        const response = await fetchApi.fetch(
          `${storageUrl}/${entityPath}/versions.json`,
        );
        const nextVersions = response.ok
          ? parseDocsVersions(await response.json())
          : [];

        if (active) {
          setVersions(nextVersions);
        }
      } catch {
        if (active) {
          setVersions([]);
        }
      }
    };

    loadVersions();

    return () => {
      active = false;
    };
  }, [
    entityRef.kind,
    entityRef.name,
    entityRef.namespace,
    fetchApi,
    techdocsStorageApi,
  ]);

  const currentVersion = useMemo(
    () => findCurrentVersion(versions, docsPath),
    [docsPath, versions],
  );

  if (!currentVersion) {
    return null;
  }

  const label = t('techDocs.versionSelector.label');

  return (
    <FormControl
      disabled={versions.length === 1}
      size="small"
      variant="outlined"
      style={{ minWidth: 200 }}
    >
      <InputLabel id="techdocs-version-selector-label">{label}</InputLabel>
      <Select
        label={label}
        labelId="techdocs-version-selector-label"
        onChange={event => {
          const version = String(event.target.value);
          if (!versions.some(candidate => candidate.version === version)) {
            return;
          }

          navigate(getVersionRootPath(location.pathname, docsPath, version));
        }}
        value={currentVersion.version}
      >
        {versions.map(version => (
          <MenuItem key={version.version} value={version.version}>
            {version.title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
